import { adminService } from "@/api/admin/admin.service";
import type {
  TConnectRequest,
  TCreateInvestorPlatformInvestorRequest,
  TInvestorPlatformInvestor,
  TUpdateInvestorPlatformInvestorRequest,
} from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 20;

const CONNECT_STATUSES = ["pending", "accepted", "dismissed"];

function companyLabel(c: TConnectRequest["fundraiser_company"]): string {
  if (!c) return "-";
  const name = typeof c.name === "string" ? c.name : "";
  const id = typeof c.id === "string" ? c.id : "";
  return name || id || "-";
}

type TInvestorEditorState = {
  open: boolean;
  investor: TInvestorPlatformInvestor | null;
  investorPlatformId: string;
  companyName: string;
  contactName: string;
  website: string;
  email: string;
  telephone: string;
};

const EMPTY_INVESTOR_EDITOR: TInvestorEditorState = {
  open: false,
  investor: null,
  investorPlatformId: "",
  companyName: "",
  contactName: "",
  website: "",
  email: "",
  telephone: "",
};

// ── Investors tab ──────────────────────────────────────────────────────────
function InvestorsTab() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [editor, setEditor] = useState<TInvestorEditorState>(EMPTY_INVESTOR_EDITOR);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<TInvestorPlatformInvestor | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  // Backend-whitelisted sort column for admin/deal-network/investors: created_at.
  const fetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getInvestorPlatformInvestors(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const openCreate = () => setEditor({ ...EMPTY_INVESTOR_EDITOR, open: true });

  const openEdit = (investor: TInvestorPlatformInvestor) =>
    setEditor({
      open: true,
      investor,
      investorPlatformId: investor.investor_platform_id ?? "",
      companyName: investor.company_name ?? "",
      contactName: investor.contact_name ?? "",
      website: investor.website ?? "",
      email: investor.email ?? "",
      telephone: investor.telephone ?? "",
    });

  const handleSave = async () => {
    if (!editor.investor && !editor.investorPlatformId.trim()) {
      toast({ variant: "destructive", title: "Investor platform ID is required" });
      return;
    }
    setIsSaving(true);
    if (editor.investor) {
      const payload: TUpdateInvestorPlatformInvestorRequest = {
        company_name: editor.companyName.trim() || null,
        contact_name: editor.contactName.trim() || null,
        website: editor.website.trim() || null,
        email: editor.email.trim() || null,
        telephone: editor.telephone.trim() || null,
      };
      if (editor.investorPlatformId.trim()) payload.investor_platform_id = editor.investorPlatformId.trim();
      const { error: err } = await adminService.updateInvestorPlatformInvestor(editor.investor.id, payload);
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to update investor",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Investor updated" });
        setEditor(EMPTY_INVESTOR_EDITOR);
        reload();
      }
    } else {
      const payload: TCreateInvestorPlatformInvestorRequest = {
        investor_platform_id: editor.investorPlatformId.trim(),
        company_name: editor.companyName.trim() || null,
        contact_name: editor.contactName.trim() || null,
        website: editor.website.trim() || null,
        email: editor.email.trim() || null,
        telephone: editor.telephone.trim() || null,
      };
      const { error: err } = await adminService.createInvestorPlatformInvestor(payload);
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to create investor",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Investor created" });
        setEditor(EMPTY_INVESTOR_EDITOR);
        reload();
      }
    }
    setIsSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const { error: err } = await adminService.deleteInvestorPlatformInvestor(deleteTarget.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete investor",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Investor deleted" });
      setDeleteTarget(null);
      reload();
    }
    setIsDeleting(false);
  };

  const columns: TColumnDef<TInvestorPlatformInvestor>[] = [
    {
      id: "company_name",
      header: "Company",
      cell: (inv) => (
        <div>
          <div className="font-medium text-foreground">{inv.company_name ?? "-"}</div>
          <div className="font-mono text-xs text-muted-foreground">{inv.id}</div>
        </div>
      ),
    },
    { id: "contact_name", header: "Contact", accessor: (inv) => inv.contact_name ?? "-" },
    {
      id: "platform",
      header: "Platform",
      accessor: (inv) => inv.platform?.name ?? inv.investor_platform_id,
    },
    { id: "email", header: "Email", type: "email", accessor: (inv) => inv.email ?? "-" },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Investors captured from external platforms</p>
      </div>

      <DataGrid<TInvestorPlatformInvestor>
        getRowId={(row) => row.id}
        persistKey="admin.investors"
        ariaLabel="Investor platform investors"
        data={{ mode: "server", fetcher, deps: [reloadTick] }}
        columns={columns}
        pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
        toolbar={{
          search: true,
          refresh: true,
          create: { label: "New Investor", onClick: openCreate, icon: <Plus className="h-4 w-4" /> },
          columnVisibility: true,
          density: true,
          export: ["csv", "json"],
          searchPlaceholder: "Search company, contact, or email",
        }}
        rowActions={(inv) => [
          { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => openEdit(inv) },
          {
            label: "Delete",
            icon: <Trash2 className="h-4 w-4" />,
            variant: "destructive",
            onClick: () => setDeleteTarget(inv),
          },
        ]}
        emptyState={{ title: "No investors found." }}
      />

      {/* Create/Edit dialog */}
      <Dialog
        open={editor.open}
        onOpenChange={(open) => {
          if (!open) setEditor(EMPTY_INVESTOR_EDITOR);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editor.investor ? "Edit Investor" : "New Investor"}</DialogTitle>
            <DialogDescription>Investor platform record details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Investor platform ID
              </label>
              <Input
                value={editor.investorPlatformId}
                onChange={(e) => setEditor((s) => ({ ...s, investorPlatformId: e.target.value }))}
                placeholder="UUID of the investor platform"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Company name</label>
                <Input
                  value={editor.companyName}
                  onChange={(e) => setEditor((s) => ({ ...s, companyName: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Contact name</label>
                <Input
                  value={editor.contactName}
                  onChange={(e) => setEditor((s) => ({ ...s, contactName: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Website</label>
              <Input
                value={editor.website}
                onChange={(e) => setEditor((s) => ({ ...s, website: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Email</label>
                <Input
                  value={editor.email}
                  onChange={(e) => setEditor((s) => ({ ...s, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Telephone</label>
                <Input
                  value={editor.telephone}
                  onChange={(e) => setEditor((s) => ({ ...s, telephone: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(EMPTY_INVESTOR_EDITOR)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Investor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete “{deleteTarget?.company_name ?? deleteTarget?.id}”? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Connect requests tab ─────────────────────────────────────────────────────
function ConnectRequestsTab() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [statusTarget, setStatusTarget] = useState<TConnectRequest | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  // Backend-whitelisted sort columns for admin/deal-network/connect-requests: created_at, status.
  const fetcher = async (query: TDataGridQuery) => {
    const status = query.filters.status;
    const response = await adminService.getConnectRequests(
      {
        limit: query.limit,
        offset: query.offset,
        ...(typeof status === "string" && status ? { status } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const openStatus = (req: TConnectRequest) => {
    setStatusTarget(req);
    setNewStatus(req.status?.name ?? "pending");
  };

  const handleUpdateStatus = async () => {
    if (!statusTarget || !newStatus) return;
    setIsUpdating(true);
    const { error: err } = await adminService.updateConnectRequestStatus(statusTarget.id, {
      status: newStatus,
    });
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to update status",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Connect request updated" });
      setStatusTarget(null);
      reload();
    }
    setIsUpdating(false);
  };

  const columns: TColumnDef<TConnectRequest>[] = [
    { id: "fundraiser", header: "Fundraiser", accessor: (req) => companyLabel(req.fundraiser_company) },
    { id: "investor", header: "Investor", accessor: (req) => companyLabel(req.investor_company) },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortKey: "status",
      cell: (req) => <Badge variant="secondary">{req.status?.name ?? "-"}</Badge>,
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    {
      id: "status",
      type: "select",
      label: "Status",
      options: CONNECT_STATUSES.map((s) => ({ label: s, value: s })),
    },
  ];

  return (
    <div className="space-y-4">
      <DataGrid<TConnectRequest>
        getRowId={(row) => row.id}
        persistKey="admin.connect-requests"
        ariaLabel="Connect requests"
        data={{ mode: "server", fetcher, deps: [reloadTick] }}
        columns={columns}
        filters={filters}
        pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
        toolbar={{
          refresh: true,
          columnVisibility: true,
          density: true,
          export: ["csv", "json"],
        }}
        rowActions={(req) => [{ label: "Update status", onClick: () => openStatus(req) }]}
        emptyState={{ title: "No connect requests found." }}
      />

      {/* Status update dialog */}
      <Dialog
        open={!!statusTarget}
        onOpenChange={(open) => {
          if (!open) setStatusTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Connect Request</DialogTitle>
            <DialogDescription>Change the status of this connect request.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {CONNECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusTarget(null)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button onClick={() => void handleUpdateStatus()} disabled={isUpdating}>
              {isUpdating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type TTab = "investors" | "requests";

export function InvestorPlatformPage() {
  const [tab, setTab] = useState<TTab>("investors");

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Investor Platform</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Curate deal-network investors and oversee connect requests
          </p>
        </div>

        <div className="flex gap-2 border-b border-border">
          <button
            type="button"
            onClick={() => setTab("investors")}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === "investors"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Investors
          </button>
          <button
            type="button"
            onClick={() => setTab("requests")}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === "requests"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Connect Requests
          </button>
        </div>

        {tab === "investors" ? <InvestorsTab /> : <ConnectRequestsTab />}
      </div>
    </div>
  );
}
